import React, { useEffect, useState } from 'react'
import BusinessIcon from '@mui/icons-material/Business'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  type StyleProps,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useAuth0 } from '@auth0/auth0-react'
import { useExportJobs } from '@containers/export-items/export-items-state'
import AdminSettingsDialog from '@containers/main/admin/admin-settings'
import { useAdminSettings } from '@containers/main/admin/admin-settings-state'
import { adminViewOptions } from '@containers/main/admin/admin-views'
import observabilityProvider from '@core/api/observability'
import { routeMap } from '@core/constants/routes'
import env from '@core/env'
import { useIsExportEnabled, useMainState, useViewMode } from '@core/main-state'
import { normalizeLanguageCode, SUPPORTED_LANGUAGES } from '@core/utils/language'

import { useSetViewMode, VIEW_MODE_LABEL_KEYS } from './view-mode-api'
import ViewModeSwitcher from './view-mode-switcher'

const styles = {
  internalIcon: {
    mr: 1,
    color: 'text.grayishBlue',
  },
} satisfies StyleProps

const idlePrefix = ['local', 'dev', 'qa'].includes(env.ENV) ? '😴 ' : ''
const envPrefix = ['local', 'dev', 'qa', 'staging'].includes(env.ENV)
  ? `[${env.ENV.toUpperCase()}] `
  : ''

// Escape hatch #2: gated on `isPreviewing` and nothing else, and never
// disabled — this is the control a staff member stuck in a customer mode
// reaches for first, so it must not depend on a role or a pending flag.
// Split out so the `useSetViewMode` mutation observer only mounts for a
// session that is actually previewing, mirroring the switcher's own gate.
const ExitPreviewMenuItem = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation('common', { keyPrefix: 'settings' })
  const setViewMode = useSetViewMode()

  return (
    <MenuItem
      onClick={() => {
        setViewMode.mutate('internal_admin')
        onClose()
      }}
    >
      <ListItemText primary={t('view_mode_exit')} />
    </MenuItem>
  )
}

const UserProfile = () => {
  const { logout, user: auth0User } = useAuth0()

  const [anchorEl, setAnchorEl] = useState(null)

  const [isIdle, user, customer] = useMainState((state) => [
    state.isIdle,
    state.user,
    state.customer,
  ])

  const isExportEnabled = useIsExportEnabled()

  const updateExportDialog = useExportJobs((state) => state.updateDialog)

  const adminSettings = useAdminSettings()

  const handleExpandClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleExpandClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    observabilityProvider.setUser?.(null)
    handleExpandClose()

    let returnTo = window.location.origin

    if (customer?.ssoConnectionName) {
      returnTo = `${window.location.origin}/login/sso?connectionName=${customer.ssoConnectionName}`
    }

    logout({ logoutParams: { returnTo } })
  }

  const { internalAdmin = false } = user || {}
  const showLanguageSelector = internalAdmin || customer?.features?.language_selector
  const { i18n, t } = useTranslation('common', { keyPrefix: 'settings' })

  const { viewMode, isPreviewing } = useViewMode()

  useEffect(() => {
    if (internalAdmin && customer?.name) {
      document.title = `${isIdle ? idlePrefix : ''}${envPrefix}${customer.name} - Finetune Generate`
    }
  }, [internalAdmin, customer?.name, isIdle])

  const viewModeBadgeLabel = viewMode
    ? t('view_mode_banner', { mode: t(VIEW_MODE_LABEL_KEYS[viewMode]) })
    : ''

  const options = internalAdmin ? adminViewOptions : []

  return (
    <Box
      display="flex"
      alignItems="center"
      sx={(theme) => ({
        '& .notifications-icon': {
          fill: theme.palette.primary.main,
        },
      })}
    >
      {user && (
        <Tooltip title={viewModeBadgeLabel}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            variant="dot"
            color="warning"
            invisible={!isPreviewing}
            data-testid="view-mode-badge"
            aria-label={isPreviewing ? viewModeBadgeLabel : undefined}
          >
            <Avatar
              alt={user.firstName || auth0User?.name}
              src={auth0User?.picture || user.picture || undefined}
              data-testid="user-profile-image"
              sx={(theme) => ({
                width: 24,
                height: 24,
                mr: 1,
                bgcolor: theme.palette.grey[600],
              })}
            />
          </Badge>
        </Tooltip>
      )}
      <IconButton
        color="primary"
        aria-label={t('aria_profile_menu')}
        aria-haspopup="true"
        onClick={handleExpandClick}
        data-testid="user-profile-expand"
        size="large"
      >
        <ExpandMoreIcon />
      </IconButton>
      <Menu
        id="user-profile-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleExpandClose}
        data-testid="user-profile-menu"
        variant="selectedMenu"
      >
        {user && (
          <ListItem>
            <ListItemText
              primary={user.email}
              secondary={user.internalAdmin ? customer?.name : ''}
            />
          </ListItem>
        )}

        <ViewModeSwitcher />

        {isPreviewing && (
          <>
            <ExitPreviewMenuItem onClose={handleExpandClose} />
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {isExportEnabled && (
          <MenuItem
            onClick={() => {
              updateExportDialog({ open: true, tab: 0, selectedJobId: null })
              handleExpandClose()
            }}
          >
            <ListItemText primary="My Exports" />
          </MenuItem>
        )}

        {internalAdmin && (
          <Divider sx={{ color: 'text.grayishBlue' }}>{t('admin_settings')}</Divider>
        )}

        {options.map(({ label, key, icon }) => (
          <MenuItem
            key={key}
            onClick={() => {
              adminSettings.setState({ open: true, view: key })
              handleExpandClose()
            }}
          >
            <Box component={icon} sx={styles.internalIcon} />
            <ListItemText primary={label} />
          </MenuItem>
        ))}

        {internalAdmin && (
          <MenuItem
            component={Link}
            to={routeMap.INTERNAL_CUSTOMERS_ROUTE}
            onClick={handleExpandClose}
          >
            <Box component={BusinessIcon} sx={styles.internalIcon} />
            <ListItemText primary="Customers" />
          </MenuItem>
        )}

        {showLanguageSelector && (
          <>
            <Divider sx={{ color: 'text.grayishBlue', mt: 2 }}>{t('language')}</Divider>
            <Box sx={{ px: 2, py: 1 }}>
              <Autocomplete
                size="small"
                options={SUPPORTED_LANGUAGES}
                value={SUPPORTED_LANGUAGES.find(
                  (l) => l.code === normalizeLanguageCode(i18n.language),
                )}
                onChange={(_, newValue) => {
                  if (newValue) {
                    i18n.changeLanguage(newValue.code)
                  }
                }}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => <TextField {...params} placeholder="Search language..." />}
                disableClearable
                sx={{ minWidth: 200 }}
              />
            </Box>
            <Divider sx={{ my: 2 }} />
          </>
        )}

        <MenuItem onClick={handleLogout}>{t('logout')}</MenuItem>
      </Menu>

      {internalAdmin && <AdminSettingsDialog adminSettings={adminSettings} />}
    </Box>
  )
}

export default UserProfile
